import React, { memo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Select, { components } from "react-select";
import styles from "./styles.module.scss";
import Button from "app/components/Button/ButtonVariant";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import closeIcon from "../../../../public/images/close-icon.svg";
import { StudentProgramRegistrationModel } from "app/models/scheduling/StudentProgramRegistrationModel";
import { schedulingFetchPatch } from "app/utils/request/scheduling/fetch";
import { isRegistrationPaymentLocked } from "app/utils/registrationPaymentUtils";
import {
  OnlinePaymentTypeOptions,
  getChargeTimingOptions,
  PaymentStatusOptions,
} from "app/routes/Calendar/EventForm/paymentHelpers";
import { SelectStyles } from "app/routes/Calendar/EventForm/constants";
import { toast } from "react-toastify";
import UserAvatar from "../UserAvatar";

interface ManageRegistrationPaymentModalProps {
  registration: StudentProgramRegistrationModel | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  collectType: string;
  stripeIntegrationEnabled: boolean;
}

const paymentMethodOptions = [
  { value: "online", label: "Online Payment" },
  { value: "offline", label: "Offline Payment" },
];

const filteredPaymentStatusOptions = PaymentStatusOptions.filter(
  (o) => o.value !== "failed" && o.value !== "past_due"
);

// All possible payment statuses for display (including online-managed statuses)
const AllPaymentStatusOptions = [
  ...PaymentStatusOptions,
  { value: "pending", label: "Pending", color: "#FFA000" },
  { value: "pending_payment", label: "Pending Payment", color: "#FFA000" },
  { value: "payment_confirmed", label: "Payment Confirmed", color: "#43A047" },
  { value: "payment_expired", label: "Payment Expired", color: "#E54500" },
];

const PaymentStatusSingleValue = ({ data, ...rest }: any) => (
  <components.SingleValue {...rest} data={data}>
    <div style={{ display: "flex", alignItems: "center" }}>
      <span
        className={styles.payment_status_dot}
        style={{ backgroundColor: data.color }}
      />
      {data.label}
    </div>
  </components.SingleValue>
);

const PaymentStatusOption = ({ data, innerRef, innerProps, isSelected }: any) => (
  <div
    ref={innerRef}
    {...innerProps}
    style={{
      padding: "10px 12px",
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      backgroundColor: isSelected ? "#f0f0f0" : "white",
    }}
  >
    <span
      className={styles.payment_status_dot}
      style={{ backgroundColor: data.color }}
    />
    {data.label}
  </div>
);

const ManageRegistrationPaymentModal = ({
  registration,
  isOpen,
  onClose,
  onSaved,
  collectType,
  stripeIntegrationEnabled,
}: ManageRegistrationPaymentModalProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"online" | "offline">("offline");
  const [onlinePaymentType, setOnlinePaymentType] = useState<"send_invoice" | "card_on_file">("send_invoice");
  const [chargeTiming, setChargeTiming] = useState<"immediate" | "after_session">("immediate");
  const [paymentStatus, setPaymentStatus] = useState<string>("unpaid");

  const locked = registration ? isRegistrationPaymentLocked(registration) : false;
  const isWaitlisted = registration?.status === "on_waitlist";

  const onlinePaymentTypeDropdownOptions = OnlinePaymentTypeOptions.filter(
    (opt) => opt.value !== "checkout"
  );
  const chargeTimingOptions = getChargeTimingOptions(onlinePaymentType);

  const availablePaymentMethods = paymentMethodOptions.filter((opt) => {
    if (opt.value === "online" && !stripeIntegrationEnabled) { return false; }
    if (opt.value === "online" && collectType === "offline") { return false; }
    if (opt.value === "offline" && collectType === "online" && stripeIntegrationEnabled) { return false; }
    return true;
  });

  const isPaymentMethodDisabled = availablePaymentMethods.length <= 1 || locked;

  const handlePaymentMethodChange = (method: "online" | "offline") => {
    setPaymentMethod(method);
    if (method === "online") {
      setPaymentStatus("pending");
    } else {
      setPaymentStatus("unpaid");
    }
  };

  useEffect(() => {
    if (registration) {
      setPaymentMethod(registration.paymentMethod || "offline");
      setOnlinePaymentType(registration.onlinePaymentType || "send_invoice");
      setChargeTiming(registration.chargeTiming || "immediate");
      setPaymentStatus(registration.paymentStatus || "unpaid");
      setError(null);
    }
  }, [registration]);

  const handleSave = async () => {
    if (!registration) { return; }

    setLoading(true);
    setError(null);

    const payload: any = {
      payment_method: paymentMethod,
    };

    if (paymentMethod === "online") {
      payload.online_payment_type = onlinePaymentType;
      payload.charge_timing = chargeTiming;
    } else if (!isWaitlisted) {
      payload.payment_status = paymentStatus === "unpaid" ? "pending" : paymentStatus;
    }

    try {
      await schedulingFetchPatch(
        `/student_program_registrations/${registration.registrationId}`,
        payload
      );
      toast.success(t("Payment updated successfully"));
      onClose();
      onSaved();
    } catch (err) {
      console.error("Failed to update registration payment:", err);
      if (err && err.errors && Array.isArray(err.errors)) {
        const messages = err.errors
          .flatMap((e: any) => (e.details || []).map((d: any) => d.message))
          .filter(Boolean);
        setError(messages.join(", ") || t("An unexpected error has occurred."));
      } else if (err.errors?.message) {
        setError(err.errors.message);
      } else {
        toast.error(t("An unexpected error has occurred."));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!registration) { return null; }

  const { firstName, lastName, email, profilePicture } = registration.studentProfile || {} as any;

  const getLockMessage = () => {
    const status = registration.paymentStatus;
    if (status === "paid" || status === "payment_confirmed") {
      return t("This registration has been paid and cannot be modified.");
    }
    if (
      registration.onlinePaymentType === "card_on_file" &&
      registration.chargeTiming === "immediate"
    ) {
      return t("A charge is in progress and cannot be modified.");
    }
    return "";
  };

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      classNames={`${styles.modal} event_form_container`}
    >
      <div className={styles.modal_header}>
        <h2>{t("Manage Payment")}</h2>
        <img
          src={closeIcon}
          alt="close"
          className={styles.close_icon}
          onClick={onClose}
        />
      </div>

      <div className={styles.form_container}>
        {locked && (
          <div className={styles.locked_banner}>
            {getLockMessage()}
          </div>
        )}

        {error && (
          <div className={styles.error_banner}>
            {error}
          </div>
        )}

        <div className={styles.athlete_card}>
          <UserAvatar
            src={profilePicture}
            height={40}
            width={40}
            first_name={firstName}
            last_name={lastName}
          />
          <div className={styles.athlete_info}>
            <span className={styles.athlete_name}>
              {firstName} {lastName}
            </span>
            {email && <span className={styles.athlete_email}>{email}</span>}
          </div>
        </div>

        <div className={styles.field_group}>
          <Select
            options={availablePaymentMethods}
            value={availablePaymentMethods.find((o) => o.value === paymentMethod)}
            onChange={(selected: any) => handlePaymentMethodChange(selected?.value)}
            styles={SelectStyles}
            menuPortalTarget={document.body}
            isSearchable={false}
            isDisabled={isPaymentMethodDisabled}
          />

          {paymentMethod === "online" && (
            <>
              <Select
                options={onlinePaymentTypeDropdownOptions}
                value={onlinePaymentTypeDropdownOptions.find(
                  (opt) => opt.value === onlinePaymentType
                )}
                onChange={(selected: any) =>
                  setOnlinePaymentType(selected?.value)
                }
                styles={SelectStyles}
                menuPortalTarget={document.body}
                isSearchable={false}
                isDisabled={locked}
              />
              <Select
                options={chargeTimingOptions}
                value={
                  chargeTimingOptions.find(
                    (opt) => opt.value === chargeTiming
                  ) || chargeTimingOptions[0]
                }
                onChange={(selected: any) =>
                  setChargeTiming(selected?.value)
                }
                styles={SelectStyles}
                menuPortalTarget={document.body}
                isSearchable={false}
                isDisabled={locked}
              />
            </>
          )}

          <Select
            options={paymentMethod === "offline" ? filteredPaymentStatusOptions : AllPaymentStatusOptions}
            value={
              AllPaymentStatusOptions.find(
                (opt) => opt.value === paymentStatus
              ) || filteredPaymentStatusOptions[0]
            }
            onChange={(selected: any) =>
              setPaymentStatus(selected?.value)
            }
            styles={SelectStyles}
            menuPortalTarget={document.body}
            isSearchable={false}
            isDisabled={locked || paymentMethod === "online" || isWaitlisted}
            components={{
              SingleValue: PaymentStatusSingleValue,
              Option: PaymentStatusOption,
            }}
          />
        </div>
      </div>

      <div className={styles.button_wrapper}>
        <Button
          buttonType="light-primary"
          title={t("Cancel")}
          onClickFunc={onClose}
        />
        <Button
          buttonType="primary"
          title={loading ? t("Saving...") : t("Save")}
          onClickFunc={handleSave}
          disabled={loading || locked}
        />
      </div>
    </ModalLayout>
  );
};

export default memo(ManageRegistrationPaymentModal);
